
import { EventType } from '.';
import { MessageType, SystemOptionType } from '../enum';
import { ArticleSystemOption, BaseEvent, BaseExternalEvent, BaseMessage, ConnectToAgent, MessageListItem, NoneOfTheAbove, ResolutionConfig, SystemOptionEvent } from '../types';
import { isInteractiveMessage } from './ItemType';

interface MessageListItemLike extends Partial<MessageListItem> { }

export function getInteractivePart (message: BaseMessage, part: 0 | 1, def = '') {
  return isInteractiveMessage(message) ? message.interactiveMessageParts[part] : def;
}

/**
 * Build a message history for the new message.
 * @param event The event to create the message history from.
 * @param historicMessage The message that triggered this event.
 * @param append Any messages to append to the history for information purposes.
 * @returns An array of message history.
 */
export function createMessageHistory (event: BaseEvent | BaseExternalEvent, historicMessage: BaseMessage, ...append: BaseMessage[]) {
  const { history = [] } = historicMessage;
  return [...history, { message: historicMessage, event }, ...append.map(m => ({ message: m }))];
}

/**
 * Similar to `Array.findIndex`, returns index if the message exists, or returns `-1` if does not exist.
 * @param messages List of messages.
 * @param toFind The message object to find.
 */
export function findMessageIndex (messages: MessageListItem[], toFind: MessageListItemLike) {
  return messages.findIndex(m => isMessageEqual(m, toFind));
}

/**
 * Works like `Array.filter` to remove a message from the list of messages.
 * @param messages The messages to delete from.
 * @param toDelete The object to delete.
 * @returns A new list of messages with the message removed.
 */
export function deleteMessage (messages: MessageListItem[], toDelete: MessageListItemLike) {
  return messages.filter(m => !isMessageEqual(m, toDelete));
}

/**
 * Compare two messages to check if they are equal. This follows the specification in {@link MessageListItem#id}.
 * @see MessageListItem#id The specification is implemented here.
 */
export function isMessageEqual (lhs: MessageListItemLike, rhs: MessageListItemLike) {
  return lhs === rhs || (lhs.id && rhs.id && (lhs.id === rhs.id));
}

/**
 * Returns `true` if the message exists.
 * @param messages The messages "haystack"
 * @param toFind The "needle" to find
 */
export function hasMessage (messages: MessageListItem[], toFind: MessageListItemLike) {
  return findMessageIndex(messages, toFind) >= 0;
}

/**
 * Get the selected category from the message history.
 * @param history The history to search for
 * @param defaultValue The default value to return if not found.
 */
export function getCategoryOptionFromMessageHistory (history: BaseMessage['history'], defaultValue: object | null = null) {
  return (history?.find(h => EventType.isOptionEvent(h.event) && h.message.type === MessageType.CATEGORIES_LIST)?.event as SystemOptionEvent)?.payload?.selected || defaultValue;
}

/**
 * Get the selected category from the message history.
 * @param history The history to search for
 * @param defaultValue The default value to return if not found.
 */
export function getCategoryTitleFromMessageHistory (history: BaseMessage['history'], defaultValue = '') {
  return getCategoryOptionFromMessageHistory(history, {})?.title || defaultValue;
}

/**
 * Get the title of the section that was last selected from message history.
 * @param history The history to search for
 * @param defaultValue The default value to return if not found.
 */
export function getSectionTitleFromMessageHistory (history: BaseMessage['history'], defaultValue = '') {
  // Find the last selected section title
  const option: null | SystemOptionEvent = (history?.reverse().find(h => EventType.isOptionEvent(h.event) && h.message.type === MessageType.SECTION_LIST)?.event as SystemOptionEvent);
  return option?.payload?.selected?.title || defaultValue;
}

export function buildResolution (source: { showEndMessage?: boolean; endMessage?: string }): ResolutionConfig {
  return {
    show: !!source.showEndMessage,
    message: source.endMessage || '',
  };
}

export function buildConnectConfig (source: {
  connectToAgent?: boolean;
  loginRequired?: boolean;
  showLiveChat?: boolean;
  showOpenRequest?: boolean;
  showVoiceChat?: boolean;
  transactionMessage?: string;
}): ConnectToAgent {
  return {
    connect: !!source.connectToAgent,
    loginRequired: !!source.loginRequired,
    liveChat: !!source.showLiveChat,
    openRequest: !!source.showOpenRequest,
    voiceChat: !!source.showVoiceChat,
    transactionMessage: source.transactionMessage,
  };
}

export function buildNoneOfTheAboveConfig (source: {
  connectToAgent?: boolean;
  loginRequired?: boolean;
  showLiveChat?: boolean;
  showOpenRequest?: boolean;
  showVoiceChat?: boolean;
  transactionMessage?: string;
  showToEndUser: boolean;
  noneOfTheAbove: boolean;
  responseMessage?: string;
  showEndMessage?: boolean;
  endMessage?: string;
}): NoneOfTheAbove {
  return {
    show: source.noneOfTheAbove && source.showToEndUser,
    showResponseMessage: !!source.responseMessage,
    responseMessage: source.responseMessage,
    config: {
      connect: buildConnectConfig(source),
      resolution: buildResolution(source),
    },
  };
}

export function buildArticleOption (source: {
  id: number;
  title: string;
  showToEndUser?: boolean;
  checks?: string | null;
  action?: string | null;
  actionMessage?: string | null;
  actionText?: string|null;
  transactionType?: string | null;
  transactionTypes?: string[];
  showDescriptionBox?: boolean | null;
  descriptionMessage?: string;
  hideContent?: boolean | null;
  category?: string | null;
  section?: string | null;
}): ArticleSystemOption {
  return {
    kind: SystemOptionType.ARTICLE,
    id: source.id,
    title: source.title,
    hideContent: source.hideContent || false,
    transactionType: source.transactionType || '',
    transactionTypes: source.transactionTypes || [],
    showDescriptionBox: source.showDescriptionBox ?? true,
    descriptionMessage: source.descriptionMessage || '',
    ...(source.checks ? { checks: source.checks } : {}),
    ...(source.action ? { action: { action: source.action, actionMessage: source.actionMessage || '', actionTitle: source.actionText || '' } } : {}),
    ...(source.category ? { category: source.category } : { category: '' }),
    ...(source.section ? { section: source.section } : { section: '' }),
  };
}

export function buildConnectToAgentWithArticleConfig(source: ConnectToAgent, articleConfig: ArticleSystemOption): ConnectToAgent {
  return {
    ...source,
    ...articleConfig,
  };
}